import type {
  DrawResult,
  ItemContent,
  RewardDelivery,
} from '~/services/referral'
import type { CurrencyDelta } from './reward-reveal'
import type { RewardVisual } from './reward-visuals'

/**
 * The milestone's client-owned face: its visual mapping and artwork
 * (GLOSSARY.md "Reward milestone"). Absent for the bind reward and for
 * milestone positions beyond the hardcoded five.
 */
export interface NominalReward {
  visual: RewardVisual
  imageUrl?: string
}

/** Client artwork for currency rewards that arrive without a nominal face
 *  (bind reward, draw conversions). */
export interface CurrencyArt {
  coins: string
  gems: string
}

/**
 * What the reward reveal presents. The visual follows the delivery — the
 * hybrid rule: client assets for the look wherever they apply, the delivery
 * for what was actually granted (GLOSSARY.md "Reward delivery").
 */
export interface RevealContent {
  visual: RewardVisual
  imageUrl?: string
  /** Backend l10n key to resolve when the content name isn't client-known. */
  titleKey?: string
  delta: CurrencyDelta
}

function kindOf(content: ItemContent): RewardVisual['kind'] {
  switch (content.type) {
    case 'coin':
      return 'coins'
    case 'gem':
      return 'gems'
    default:
      return 'content'
  }
}

function deltaOf(contents: ItemContent[]): CurrencyDelta {
  let coin = 0
  let gem = 0
  for (const content of contents) {
    if (content.type === 'coin') coin += content.value
    if (content.type === 'gem') gem += content.value
  }
  return { coin, gem }
}

/** A draw counts as a conversion only when the final prize differs from the
 *  original (e.g. a duplicate item turned into coins) — a draw that landed
 *  its original prize did not convert anything. */
function isConversion(draw: DrawResult): boolean {
  const { original_prize: o, final_prize: f } = draw
  return o.type !== f.type || o.value !== f.value || o.title_key !== f.title_key
}

export function deriveRevealContent(
  delivery: RewardDelivery,
  nominal: NominalReward | undefined,
  currencyArt: CurrencyArt,
): RevealContent {
  // A converted draw's final prize is what actually landed and replaces the
  // nominal face; otherwise the first grant leads the celebration (bonus
  // grants still count into the delta).
  const conversion = delivery.draw_results.find(isConversion)
  const primary = conversion?.final_prize ?? delivery.granted_contents[0]
  // The delta always comes from what was granted — never the nominal mapping,
  // so we never credit currency the server didn't deliver.
  const delta = deltaOf(delivery.granted_contents)

  if (!primary) {
    // Defensive: nothing delivered to present (the reveal only opens after a
    // delivery). Show the nominal face but credit nothing.
    if (!nominal) return { visual: { kind: 'content', name: '' }, delta }
    return { visual: nominal.visual, imageUrl: nominal.imageUrl, delta }
  }

  const kind = kindOf(primary)

  if (nominal && nominal.visual.kind === kind && !conversion) {
    return {
      visual:
        kind === 'content' ? nominal.visual : { kind, amount: primary.value },
      imageUrl: nominal.imageUrl,
      delta,
    }
  }

  if (kind === 'content') {
    return {
      visual: { kind, name: '' },
      titleKey: primary.title_key || undefined,
      imageUrl: primary.icon_image_url || undefined,
      delta,
    }
  }
  return {
    visual: { kind, amount: primary.value },
    imageUrl:
      (kind === 'coins' ? currencyArt.coins : currencyArt.gems) ||
      primary.icon_image_url ||
      undefined,
    delta,
  }
}
